import {
  INDUSTRY_KEYS,
  IndustrySlug
} from 'containers/Setting/components/RoleFormModal/customerIntelligence';

import { CardDescriptor } from '../types';

// `hasOwnProperty.call` rather than `in`: `in` walks the prototype chain, so a tenant industry of
// 'constructor' or 'toString' would resolve to a function and throw on the lookup below
// (`conventions/components/cross-browser.md § 9` rules out `Object.hasOwn`). Same guard as
// `permissions/useCardVisibility.ts`. PLA-8815.
export const isIndustrySlug = (industry?: string | null): industry is IndustrySlug =>
  !!industry && Object.prototype.hasOwnProperty.call(INDUSTRY_KEYS, industry);

// The tenant's industry decides which cards of a band exist at all, and the frozen PLA-8807 catalog
// is the only map of that: a second hand-written industry→card table here would let one side drift
// and draw a card the role can never be granted. AF-5 is why the filter tests `industryScoped`
// rather than membership alone — `*.product_level_readiness` is deliberately absent from
// `INDUSTRY_KEYS` because Product Readiness is gated on its own key and must survive this filter.
// Generic over the section key for the same reason `useCardVisibility`'s readers are: Customer 360
// declares its own section union, this filter reads only `industryScoped` and `permissionKey`, and
// leaving the parameter fixed would have forced a second copy of the rule for the C360 band.
// Layer 1's call sites still infer `IntelligenceSectionKey` and are unchanged. PLA-8828.
export const industryCardsFor = <S>(
  cards: CardDescriptor<S>[],
  industry?: string | null
): CardDescriptor<S>[] => {
  const permitted = new Set(isIndustrySlug(industry) ? INDUSTRY_KEYS[industry] : []);

  return cards.filter(card => !card.industryScoped || permitted.has(card.permissionKey));
};

export default industryCardsFor;
